import type {
  ResourceKind,
  ResourceNode,
  ResourceRelationship,
  ResourceRelationshipDraft,
  ResourceRelationshipType,
  ResourceTopologyIssue,
  ResourceTopologyVersion,
} from '../../types/prodResourceTopology';

export const RESOURCE_KIND_LABELS: Record<ResourceKind, string> = {
  BUILDING: '建筑',
  AREA: '区域',
  SUITE: '生产套间',
  ROOM: '房间',
  EQUIPMENT: '设备',
  COMPONENT: '组件',
  CIP_STATION: 'CIP 站',
  CIP_LOOP: '逻辑 CIP 回路',
};

export const RELATIONSHIP_TYPE_LABELS: Record<ResourceRelationshipType, string> = {
  PARENT_OF: '物理父子',
  CIP_LOOP_MEMBER: '回路成员',
  CIP_PRIMARY_STATION: '回路主站',
  CIP_BACKUP_STATION: '回路备站',
};

export const relationshipEndpointLabels = (type: ResourceRelationshipType): { source: string; target: string } => {
  if (type === 'PARENT_OF') return { source: '物理父级', target: '物理子级' };
  if (type === 'CIP_LOOP_MEMBER') return { source: 'CIP 回路', target: '回路成员' };
  if (type === 'CIP_PRIMARY_STATION') return { source: 'CIP 回路', target: '主 CIP 站' };
  return { source: 'CIP 回路', target: '备 CIP 站' };
};

const relationshipResourceLabel = (resource: ResourceNode | undefined, resourceId: string): string => (
  resource ? `${resource.name}（${resource.code}）` : `未知资源（${resourceId}）`
);

/**
 * 将关系记录转换为面向业务人员的说明，不向界面暴露 source / target 这类存储方向。
 */
export const formatRelationshipStatement = (
  relationship: ResourceRelationship,
  resourceById: ReadonlyMap<string, ResourceNode>,
): string => {
  const source = relationshipResourceLabel(resourceById.get(relationship.sourceResourceId), relationship.sourceResourceId);
  const target = relationshipResourceLabel(resourceById.get(relationship.targetResourceId), relationship.targetResourceId);

  if (relationship.type === 'PARENT_OF') return `${source} 包含 ${target}`;
  if (relationship.type === 'CIP_LOOP_MEMBER') return `${target} 是 ${source} 的回路成员`;
  if (relationship.type === 'CIP_PRIMARY_STATION') return `${source} 的主 CIP 站是 ${target}`;
  return `${source} 的备 CIP 站是 ${target}${relationship.priority ? `（优先级 ${relationship.priority}）` : ''}`;
};

export interface ResourceTreeItem {
  key: string;
  resource: ResourceNode;
  children: ResourceTreeItem[];
}

const searchableText = (node: ResourceNode) => (
  `${node.code} ${node.name} ${RESOURCE_KIND_LABELS[node.kind]}`.toLocaleLowerCase()
);

export const buildResourceForest = (
  resources: ResourceNode[],
  relationships: ResourceRelationship[],
  query = '',
): ResourceTreeItem[] => {
  const byId = new Map(resources.map((node) => [node.id, node]));
  const childrenByParent = new Map<string, string[]>();
  const childIds = new Set<string>();

  relationships
    .filter((relation) => relation.type === 'PARENT_OF')
    .forEach((relation) => {
      if (!byId.has(relation.sourceResourceId) || !byId.has(relation.targetResourceId)) return;
      const children = childrenByParent.get(relation.sourceResourceId) ?? [];
      children.push(relation.targetResourceId);
      childrenByParent.set(relation.sourceResourceId, children);
      childIds.add(relation.targetResourceId);
    });

  const visiting = new Set<string>();
  const build = (resourceId: string): ResourceTreeItem | null => {
    const resource = byId.get(resourceId);
    if (!resource || visiting.has(resourceId)) return null;
    visiting.add(resourceId);
    const children = (childrenByParent.get(resourceId) ?? [])
      .map(build)
      .filter((item): item is ResourceTreeItem => Boolean(item))
      .sort((a, b) => a.resource.code.localeCompare(b.resource.code));
    visiting.delete(resourceId);
    return { key: resource.id, resource, children };
  };

  const roots = resources
    .filter((resource) => !childIds.has(resource.id))
    .map((resource) => build(resource.id))
    .filter((item): item is ResourceTreeItem => Boolean(item))
    .sort((a, b) => a.resource.code.localeCompare(b.resource.code));

  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return roots;

  const filter = (item: ResourceTreeItem): ResourceTreeItem | null => {
    const children = item.children
      .map(filter)
      .filter((child): child is ResourceTreeItem => Boolean(child));
    if (searchableText(item.resource).includes(normalizedQuery) || children.length > 0) {
      return { ...item, children };
    }
    return null;
  };

  return roots.map(filter).filter((item): item is ResourceTreeItem => Boolean(item));
};

export const relationsForResource = (
  relationships: ResourceRelationship[],
  resourceId: string | null,
): ResourceRelationship[] => {
  if (!resourceId) return relationships;
  return relationships.filter(
    (relation) => relation.sourceResourceId === resourceId || relation.targetResourceId === resourceId,
  );
};

export const parentIdForResource = (
  relationships: ResourceRelationship[],
  resourceId: string,
): string | null => (
  relationships.find(
    (relation) => relation.type === 'PARENT_OF' && relation.targetResourceId === resourceId,
  )?.sourceResourceId ?? null
);

export const parentRelationForResource = (
  relationships: ResourceRelationship[],
  resourceId: string,
): ResourceRelationship | null => (
  relationships.find(
    (relation) => relation.type === 'PARENT_OF' && relation.targetResourceId === resourceId,
  ) ?? null
);

export const nodeAttribute = (node: ResourceNode, key: string): string => {
  const value = node.attributes?.[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
};

export const isEligibleCipLoopMember = (node: ResourceNode): boolean => (
  (node.kind === 'EQUIPMENT' || node.kind === 'COMPONENT')
  && node.lifecycleStatus !== 'RETIRED'
  && node.attributes?.cipCleanable === true
);

export const validateRelationshipDraft = (
  draft: ResourceRelationshipDraft,
  resources: ResourceNode[],
): string | null => {
  const source = resources.find((resource) => resource.id === draft.sourceResourceId);
  const target = resources.find((resource) => resource.id === draft.targetResourceId);
  if (!source || !target) return '请选择完整的关系两端节点';
  if (source.id === target.id) return '关系两端不能是同一节点';
  if (draft.type === 'PARENT_OF') {
    if (source.kind === 'CIP_LOOP' || target.kind === 'CIP_LOOP') return '逻辑 CIP 回路不能加入物理层级';
    return null;
  }
  if (source.kind !== 'CIP_LOOP') return 'CIP 关系的起点必须是逻辑 CIP 回路';
  if (draft.type === 'CIP_LOOP_MEMBER' && !['EQUIPMENT', 'COMPONENT'].includes(target.kind)) {
    return '回路成员只能是设备或组件';
  }
  if (draft.type === 'CIP_LOOP_MEMBER' && !isEligibleCipLoopMember(target)) {
    return target.lifecycleStatus === 'RETIRED'
      ? '已退役资源不能新增回路成员关系'
      : '回路成员必须先标记为“可作为 CIP 回路成员”';
  }
  if (
    (draft.type === 'CIP_PRIMARY_STATION' || draft.type === 'CIP_BACKUP_STATION')
    && target.kind !== 'CIP_STATION'
  ) {
    return '主站或备站关系的目标必须是 CIP 站';
  }
  return null;
};

export const parseFacilityCodes = (raw: string | undefined): string[] => {
  const values = (raw ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  return values.length > 0 ? Array.from(new Set(values)) : ['F1'];
};

export const findActiveTopologyDraft = <T extends ResourceTopologyVersion>(versions: T[]): T | null => (
  versions.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED') ?? null
);

export const isWritableVersion = (version: ResourceTopologyVersion | null): boolean => (
  version?.status === 'DRAFT' || version?.status === 'VALIDATED'
);

export const canPublishVersion = (
  version: ResourceTopologyVersion | null,
  issues: ResourceTopologyIssue[],
): boolean => Boolean(
  version
  && version.status === 'VALIDATED'
  && version.validatedRevision === version.revision
  && !issues.some((issue) => issue.hardOrSoft === 'hard'),
);

export const relationshipEndpointKinds = (type: ResourceRelationshipType): {
  sourceKinds: ResourceKind[];
  targetKinds: ResourceKind[];
} => {
  if (type === 'PARENT_OF') {
    const physicalKinds: ResourceKind[] = ['BUILDING', 'AREA', 'SUITE', 'ROOM', 'EQUIPMENT', 'COMPONENT', 'CIP_STATION'];
    return { sourceKinds: physicalKinds, targetKinds: physicalKinds };
  }
  if (type === 'CIP_LOOP_MEMBER') {
    return { sourceKinds: ['CIP_LOOP'], targetKinds: ['EQUIPMENT', 'COMPONENT'] };
  }
  return { sourceKinds: ['CIP_LOOP'], targetKinds: ['CIP_STATION'] };
};
